/*
 * Copyright (c) 2025 curryfirm.com
 */

import { findBestStopover, measureLeg, } from './helpers.js';
import { MOVES } from '../constants.js';

/**
 * Given a sequence of waypoints (dumpster locations) to visit and a set of available dumps,
 * determine which dump to use after each waypoint such that the overall travel time is minimized.
 * This service assumes that the route begins with an empty dumpster in hand such that we swap
 * an empty for a full dumpster at each waypoint and that we do not have to return to a waypoint.
 * (We also assume that minimizing the distance of (waypoint-i to some-dump to waypoint-i+1) for
 * each pair of consecutive waypoints in the sequence minimizes the total distance of the entire route.
 * The same thinking is applied to the initial startingPoint and the final endingPoint.)
 * @param startingPoint Object with "lat" and "lng" keys
 * @param endingPoint Object with "lat" and "lng" keys
 * @param waypoints  Array of objects with "lat" and "lng" keys
 * @param dumps Array of objects with "lat" and "lng" keys
 * @param options An argument for "the rest," mostly to carry a signal to abort
 * @returns {{totalDistance: number, moves: *[], locationSequence: *[]}}
 */
const getSwapDropRoute = async (startingPoint, endingPoint, waypoints, dumps, options = {}) => {
    const { signal } = options;
    let totalLength = 0;
    const moves = [];
    let current = startingPoint;
    const locationSequence = [current];  // locationSequence includes starting and ending points.

    for (const [index, waypoint] of waypoints.entries()) {

        const length1 = await measureLeg(current, waypoint, signal);
        totalLength += length1.durationSeconds;
        moves.push({ from: current, action: MOVES.TO_DUMPSTER, to: waypoint });
        locationSequence.push(waypoint)

        // Determine best dump to visit between waypoint and its successor waypoint (or the endingPoint).
        const followingPoint = ((index + 1 < waypoints.length) ? waypoints[index + 1] : endingPoint);
        const dump = await findBestStopover(waypoint, followingPoint, dumps);

        const length2 = await measureLeg(waypoint, dump, signal);
        totalLength += length2.durationSeconds;
        moves.push({ from: waypoint, action: MOVES.TO_DUMP, to: dump });
        locationSequence.push(dump);

        current = dump;
    }

    // Last dump -> Ending point
    const length3 = await measureLeg(current, endingPoint, signal);
    totalLength += length3.durationSeconds;
    moves.push({ from: current, action: MOVES.TO_ENDING_POINT, to: endingPoint });
    locationSequence.push(endingPoint);

    return { totalLength, moves, locationSequence };
}

export { getSwapDropRoute };